import { SimpleCheckList } from '@components'

import { amountToDisplayValue, Color, Icon, SkeletonBox, Text, Tooltip } from '@billy/design-system'
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonVariant,
  Divider,
  Tag,
  TagColor,
  TagSize,
  TagType,
  Typography,
  XStack,
  YStack,
} from '@shinetools/lumen-react'
import { colors } from '@shinetools/lumen-tokens/web/colors'
import { Backpack } from 'lucide-react'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useSubscriptionPlanPrices } from '@views/settings/routes/OrganizationSubscriptionSettings/hooks/useSubscriptionPlanPrices'
import { resolveSubscriptionPlan } from '@views/settings/routes/OrganizationSubscriptionSettings/utils/resolveSubscriptionPlan'

import { useOrganizationMarket } from '../../../../hooks'
import { BACK_TO_SCHOOL_CURRENCY } from '../../constants/backToSchoolCurrency'
import { useTrackBackToSchoolCampaign } from '../../hooks/useTrackBackToSchoolCampaign'
import type { BackToSchoolOffer } from '../../types/backToSchoolOffer'
import * as Styled from './styles'

interface BackToSchoolPlanCardProps {
  offer: BackToSchoolOffer
  onSelect?: (promoPriceAmount: number) => void
}

export const BackToSchoolPlanCard = ({ offer, onSelect }: BackToSchoolPlanCardProps): ReactElement => {
  const { t } = useTranslation()
  const { market } = useOrganizationMarket()
  const { trackCampaign } = useTrackBackToSchoolCampaign()
  const { isLoading: isLoadingPrices, monthlyPrice } = useSubscriptionPlanPrices(offer.plan)
  const isHighlighted = !!offer.isHighlighted

  const { featureItems, nameKey } = useMemo(() => {
    const { content } = resolveSubscriptionPlan(offer.plan, market || undefined)
    return {
      featureItems: offer.featureKeys.map((tKey) => ({ tKey })),
      nameKey: content.name,
    }
  }, [market, offer.featureKeys, offer.plan])

  const originalYearlyPrice = monthlyPrice !== undefined ? monthlyPrice * 12 : 0

  const handleSelectClick = useCallback(() => {
    trackCampaign('Plan Selected', {
      billingPeriod: 'yearly',
      currency: BACK_TO_SCHOOL_CURRENCY,
      discountPct: offer.savePercent,
      plan: offer.plan,
      priceAmount: offer.promoPriceAmount,
      referenceAmount: originalYearlyPrice,
    })
    onSelect?.(offer.promoPriceAmount)
  }, [offer.plan, offer.promoPriceAmount, offer.savePercent, onSelect, originalYearlyPrice, trackCampaign])

  const handleShowTerms = useCallback(() => {
    trackCampaign('Terms Expanded', { plan: offer.plan })
  }, [offer.plan, trackCampaign])

  return (
    <Styled.Card isHighlighted={isHighlighted}>
      <YStack gap={16}>
        <YStack gap={8}>
          <XStack align="center" gap={16}>
            <Typography.Header as="h3">{t(nameKey)}</Typography.Header>
            <Tag
              color={TagColor.cyan}
              icon={<Backpack />}
              label={t('back_to_school.plan.save_badge', { percent: offer.savePercent })}
              size={TagSize.medium}
              type={TagType.subtle}
            />
          </XStack>
          <Typography.Text color={colors.content.neutral.medium} size="small">
            {t(offer.descriptionKey)}
          </Typography.Text>
        </YStack>

        <YStack gap={4}>
          <XStack align="baseline" gap={4}>
            <Typography.Header as="h2">{offer.promoPrice}</Typography.Header>
            <Typography.Text color={colors.content.neutral.medium} size="small">
              {t('back_to_school.plan.promo_price_vat_suffix')}
            </Typography.Text>
          </XStack>
          <XStack align="center" gap={4}>
            <Typography.Text color={colors.content.neutral.medium} size="small">
              {t('back_to_school.plan.note')}
            </Typography.Text>
            <Tooltip label={t(offer.noteTooltipKey)} onShow={handleShowTerms} placement="right">
              <Icon icon="info" size="xs" />
            </Tooltip>
          </XStack>
          {isLoadingPrices ? (
            <SkeletonBox fullWidth />
          ) : (
            originalYearlyPrice > 0 && (
              <Text color={Color.Grey400} strikethrough variant="bodySM">
                {amountToDisplayValue(originalYearlyPrice, true, BACK_TO_SCHOOL_CURRENCY)}
              </Text>
            )
          )}
        </YStack>

        <Divider visibility="low" />

        <YStack gap={12}>
          <Typography.Text size="medium" weight="semibold">
            {t(offer.featuresHeaderKey)}
          </Typography.Text>
          <SimpleCheckList
            alignment="left"
            condensed
            iconColor={colors.content.link}
            items={featureItems}
            textColor={Color.Grey800}
            textVariant="bodySM"
            variant="flat"
          />
        </YStack>

        <Button
          color={isHighlighted ? ButtonColor.accent : ButtonColor.primary}
          label={t('back_to_school.plan.button.choose_offer')}
          onClick={handleSelectClick}
          size={ButtonSize.medium}
          variant={isHighlighted ? ButtonVariant.filled : ButtonVariant.outlined}
        />
      </YStack>
    </Styled.Card>
  )
}
